import { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const POLYGON_CHAIN_ID = '0x89'; // 137 in hex (Polygon Mainnet)
const POLYGON_RPC = 'https://polygon-rpc.com';

// Placeholder Contract Address (User needs to deploy and update this)
// For now, this points to 0x0... which will just log a warning.
// Once deployed, replace this string with your contract address.
export const CONTRACT_ADDRESS = "0x0cbb2a6169299943d2b06c07dbee386c6b5cfae8"; 

// Minimal ABI for ERC-20 Minting (if we owned the contract) or Transferring
const CONTRACT_ABI = [
  "function claim(uint256 amount) public", // New public claim function
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)"
];

export function useWeb3() {
  const [account, setAccount] = useState<string | null>(null);
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [chainId, setChainId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (window.ethereum) {
      const provider = new ethers.BrowserProvider(window.ethereum);
      setProvider(provider);
      
      window.ethereum.on('accountsChanged', (accounts: string[]) => {
        setAccount(accounts[0] || null);
      });

      window.ethereum.on('chainChanged', (id: string) => {
        setChainId(id);
      });
    }
  }, []);

  const connectWallet = async () => {
    if (!window.ethereum) {
      setError("Please install Metamask or a Web3 wallet.");
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      const network = await provider.getNetwork();
      
      setAccount(accounts[0]);
      setChainId("0x" + network.chainId.toString(16));
      setProvider(provider);

      // Check if on Polygon
      if ("0x" + network.chainId.toString(16) !== POLYGON_CHAIN_ID) {
        await switchToPolygon();
      }

    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to connect");
    } finally {
      setIsConnecting(false);
    }
  };

  const switchToPolygon = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: POLYGON_CHAIN_ID }],
      });
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: POLYGON_CHAIN_ID,
                chainName: 'Polygon Mainnet',
                nativeCurrency: {
                  name: 'POL',
                  symbol: 'POL',
                  decimals: 18,
                },
                rpcUrls: [POLYGON_RPC],
                blockExplorerUrls: ['https://polygonscan.com/'],
              },
            ],
          });
        } catch (addError) {
          setError("Failed to add Polygon network");
        }
      } else {
        setError("Failed to switch to Polygon");
      }
    }
  };

  const claimTokensOnChain = async (amount: number) => {
    if (!account || !provider) return;

    try {
      const signer = await provider.getSigner();
      
      // If contract is deployed, call the claim function
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      const decimals = 18; // Default for ERC20
      
      // Convert amount to Wei
      const amountWei = ethers.parseUnits(amount.toString(), decimals);
      
      const tx = await contract.claim(amountWei);
      await tx.wait();
      return tx.hash;

    } catch (err: any) {
      console.error(err);
      throw new Error(err.message || "Transaction failed");
    }
  };

  return { 
    account, 
    connectWallet, 
    claimTokensOnChain, 
    error, 
    isConnecting,
    chainId 
  };
}

// Add window type definition
declare global {
  interface Window {
    ethereum: any;
  }
}
